import { useState } from 'react';
import { PrivacyInfo } from './PrivacyInfo';

interface Props {
  onDismiss: () => void;
}

export function OnboardingCard({ onDismiss }: Props) {
  const [open, setOpen] = useState<number | null>(0);

  function toggle(i: number): void {
    setOpen(open === i ? null : i);
  }

  return (
    <section className="rounded-md border border-slate-300 bg-slate-50 p-3 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-800">Welcome to ManuscriptSkim</h2>
        <button
          type="button"
          onClick={onDismiss}
          className="text-[10px] text-slate-500 hover:text-slate-800"
        >
          Got it, hide this
        </button>
      </div>

      <Section
        idx={0}
        open={open === 0}
        title="What this does"
        onToggle={() => toggle(0)}
      >
        <p>
          ManuscriptSkim helps board editors handle &ldquo;please skim this manuscript&rdquo;
          requests faster. When you drop a PDF in, it:
        </p>
        <ul className="mt-1 space-y-0.5">
          <li>• Identifies the study type and picks the right EQUATOR guideline (CONSORT, STROBE, PRISMA, STARD, CARE).</li>
          <li>• Locates evidence for each checklist item — you scan a table instead of re-reading the paper end-to-end.</li>
          <li>• Flags numerical inconsistencies, retractions in the bibliography, and AI-slop voice patterns.</li>
          <li>• Suggests candidate reviewers from OpenAlex and PubMed, COI-filtered against the manuscript&apos;s authors.</li>
          <li>• Drafts a decision recommendation. You confirm, edit, and export as email or PDF.</li>
        </ul>
        <p className="mt-2 text-[11px] text-slate-500">
          Advisory only. The editor&apos;s word is final.
        </p>
      </Section>

      <Section
        idx={1}
        open={open === 1}
        title="Verify privacy"
        onToggle={() => toggle(1)}
      >
        <PrivacyInfo />
      </Section>

      <Section
        idx={2}
        open={open === 2}
        title="What about the local LLM?"
        onToggle={() => toggle(2)}
      >
        <p>
          This is the <span className="font-medium">pattern-only</span> v0.5 release. Every analysis
          you&apos;ll see here is deterministic JavaScript — no LLM is loaded.
        </p>
        <p className="mt-1">
          A future v1.0 release adds an in-browser Llama-3.1-8B (one-time 5 GB download, cached
          forever) to grade each checklist item as reported / partial / missing rather than just
          located / not located. Until then, the pattern layer surfaces evidence and the editor
          judges.
        </p>
      </Section>
    </section>
  );
}

interface SectionProps {
  idx: number;
  open: boolean;
  title: string;
  children: React.ReactNode;
  onToggle: () => void;
}

function Section({ idx, open, title, children, onToggle }: SectionProps) {
  return (
    <div className="mt-2 rounded border border-slate-200 bg-white">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between px-2 py-1.5 text-left text-xs font-medium text-slate-700 hover:bg-slate-50"
      >
        <span>
          <span className="mr-2 text-slate-400">{idx + 1}.</span>
          {title}
        </span>
        <span className="text-slate-400">{open ? '−' : '+'}</span>
      </button>
      {open && <div className="border-t border-slate-200 p-2 text-xs text-slate-700">{children}</div>}
    </div>
  );
}
